import type {
    Resume,
    ResumeCertification,
    ResumeChange,
    ResumeEducation,
    ResumeRole,
} from "@/lib/api/resumeService";

/** The resume as it went, put back from its baseline and the tailor's changes. */

function educationLine(entry: ResumeEducation): string {
    return [entry.school, entry.degree, entry.year].filter(Boolean).join(", ");
}

function certificationLine(entry: ResumeCertification): string {
    return [entry.name, entry.issuer, entry.year].filter(Boolean).join(", ");
}

/** Whole entries as the tailor left them, by their lines; null when one cannot be told back. */
function entriesAfter<Entry>(
    baseline: readonly Entry[],
    line: (entry: Entry) => string,
    changes: readonly ResumeChange[],
): Entry[] | null {
    const lines = listAfter(baseline.map(line), changes);
    if (!lines) return null;
    const byLine = new Map(baseline.map((entry) => [line(entry), entry]));
    const entries = lines.map((held) => byLine.get(held));
    return entries.every((entry) => entry !== undefined) ? (entries as Entry[]) : null;
}

type ChangedRoleLike = { company: string; title: string };

/** A role's line as the resume service compares it: what the tailor may not change. */
function fullRoleLine(role: ResumeRole): string {
    return [`${role.title}, ${role.company}`, role.start, role.end, role.location]
        .filter(Boolean)
        .join(", ");
}

function sameRole(left: ChangedRoleLike, right: ResumeRole): boolean {
    return left.company === right.company && left.title === right.title;
}

/**
 * A list as the tailor left it: what was dropped taken out, the kept in
 * the tailored order when it was reordered. Null when something was
 * added, since a change does not say where it went.
 */
function listAfter(baseline: readonly string[], changes: readonly ResumeChange[]): string[] | null {
    if (changes.some((change) => change.kind === "ADDED" || change.kind === "REWORDED"))
        return null;
    const dropped = new Set(
        changes.filter((change) => change.kind === "DROPPED").flatMap((change) => change.before),
    );
    const reordered = changes.find((change) => change.kind === "REORDERED");
    return reordered ? [...reordered.after] : baseline.filter((item) => !dropped.has(item));
}

function bulletsAfter(role: ResumeRole, changes: readonly ResumeChange[]): string[] | null {
    if (changes.some((change) => change.kind === "ADDED")) return null;
    const reordered = changes.find((change) => change.kind === "REORDERED");
    if (reordered) return [...reordered.after];
    const reworded = new Map(
        changes
            .filter((change) => change.kind === "REWORDED")
            .map((change) => [change.before[0] ?? "", change.after[0] ?? ""]),
    );
    const dropped = new Set(
        changes.filter((change) => change.kind === "DROPPED").flatMap((change) => change.before),
    );
    return role.bullets
        .filter((bullet) => !dropped.has(bullet))
        .map((bullet) => reworded.get(bullet) ?? bullet);
}

/**
 * The resume as it went, put back from the version it was tailored from
 * and the tailor's changes, as the resume service does when it sets the
 * PDF. Null whenever that cannot be done exactly: something added (a
 * change does not say where it went), a role's own line changed, or two
 * roles alike. The PDF is the record; this is never a guess at it.
 */
export function sentResume(baseline: Resume, changes: readonly ResumeChange[]): Resume | null {
    const of = (section: ResumeChange["section"]) =>
        changes.filter((change) => change.section === section);

    const textAfter = (value: string | null | undefined, section: ResumeChange["section"]) => {
        const change = of(section).find((entry) => entry.kind !== "UNCHANGED");
        if (!change) return { ok: true, value: value ?? null };
        if (change.kind === "DROPPED") return { ok: true, value: null };
        if (change.kind === "REWORDED" || change.kind === "ADDED")
            return { ok: true, value: change.after[0] ?? null };
        return { ok: false, value: null };
    };

    const headline = textAfter(baseline.headline, "HEADLINE");
    const summary = textAfter(baseline.summary, "SUMMARY");
    if (!headline.ok || !summary.ok) return null;

    const skills = listAfter(baseline.skills, of("SKILLS"));
    const education = entriesAfter(baseline.education, educationLine, of("EDUCATION"));
    const certifications = entriesAfter(
        baseline.certifications ?? [],
        certificationLine,
        of("CERTIFICATIONS"),
    );
    if (!skills || !education || !certifications) return null;

    const roles = baseline.experience;
    const twins = roles.some((role, index) =>
        roles.some((other, place) => place !== index && sameRole(role, other)),
    );
    const experienceChanges = of("EXPERIENCE");
    const known = (change: ResumeChange) =>
        change.role !== undefined &&
        change.role !== null &&
        roles.some((role) => sameRole(change.role as ChangedRoleLike, role));
    if (twins || !experienceChanges.every(known)) return null;
    const experience: ResumeRole[] = [];
    for (const role of roles) {
        const own = experienceChanges.filter((change) =>
            sameRole(change.role as ChangedRoleLike, role),
        );
        const line = fullRoleLine(role);
        const lineChange = own.find(
            (change) => change.before[0] === line && change.kind !== "REORDERED",
        );
        if (lineChange?.kind === "DROPPED" && own.length === 1) continue;
        if (lineChange) return null;
        const bullets = bulletsAfter(role, own);
        if (!bullets) return null;
        experience.push({ ...role, bullets });
    }

    return {
        ...baseline,
        headline: headline.value,
        summary: summary.value,
        skills,
        experience,
        education,
        // A baseline from before certifications has no list, and the resume
        // sent from it none either.
        ...(baseline.certifications ? { certifications } : {}),
    };
}
